import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, switchMap } from 'rxjs';
import { environment } from '../../environments/environment';
import {
  CreateAgentRequest,
  CustomerUserDTO,
  InvitationDTO,
  InviteUserRequest,
  PublicInvitationDTO,
  RoleDTO,
  TenantUserDirectoryDTO,
  UpdateCustomerUserRequest,
  UpdateTenantUserStatusRequest,
} from '../models/customer.model';
import { AccountService } from '../account/account.service';

@Injectable({ providedIn: 'root' })
export class TenantUserService {
  private readonly http = inject(HttpClient);
  private readonly accountService = inject(AccountService);

  getUsers(customerId: string): Observable<CustomerUserDTO[]> {
    return this.http.get<CustomerUserDTO[]>(`${environment.apiBaseUrl}/api/v1/customers/${customerId}/users`);
  }

  getUsersDirectory(customerId: string): Observable<TenantUserDirectoryDTO[]> {
    return this.http.get<TenantUserDirectoryDTO[]>(
      `${environment.apiBaseUrl}/api/v1/customers/${customerId}/users/directory`,
    );
  }

  getUser(customerId: string, userId: string): Observable<CustomerUserDTO> {
    return this.http.get<CustomerUserDTO>(`${environment.apiBaseUrl}/api/v1/customers/${customerId}/users/${userId}`);
  }

  updateUser(customerId: string, userId: string, request: UpdateCustomerUserRequest): Observable<CustomerUserDTO> {
    return this.http.put<CustomerUserDTO>(
      `${environment.apiBaseUrl}/api/v1/customers/${customerId}/users/${userId}`,
      request,
    );
  }

  setStatus(
    customerId: string,
    userId: string,
    status: UpdateTenantUserStatusRequest['status'],
  ): Observable<CustomerUserDTO> {
    const body: UpdateTenantUserStatusRequest = { status };
    return this.http.put<CustomerUserDTO>(
      `${environment.apiBaseUrl}/api/v1/customers/${customerId}/users/${userId}/status`,
      body,
    );
  }

  getUsersForCurrentProfile(): Observable<CustomerUserDTO[]> {
    return this.accountService.getProfile().pipe(switchMap((profile) => this.getUsers(profile.customerId)));
  }

  getRoles(customerId: string): Observable<RoleDTO[]> {
    return this.http.get<RoleDTO[]>(`${environment.apiBaseUrl}/api/v1/customers/${customerId}/roles`);
  }

  assignPlatformRole(customerId: string, userId: string, roleId: string): Observable<CustomerUserDTO> {
    return this.http.put<CustomerUserDTO>(
      `${environment.apiBaseUrl}/api/v1/customers/${customerId}/users/${userId}/role`,
      { roleId },
    );
  }

  assignTenantRole(customerId: string, userId: string, roleId: string): Observable<CustomerUserDTO> {
    return this.http.put<CustomerUserDTO>(
      `${environment.apiBaseUrl}/api/v1/customers/${customerId}/users/${userId}/tenant-role`,
      { roleId },
    );
  }

  delete(customerId: string, userId: string): Observable<void> {
    return this.http.delete<void>(`${environment.apiBaseUrl}/api/v1/customers/${customerId}/users/${userId}`);
  }

  invite(customerId: string, request: InviteUserRequest): Observable<InvitationDTO> {
    return this.http.post<InvitationDTO>(
      `${environment.apiBaseUrl}/api/v1/customers/${customerId}/users/invite`,
      request,
    );
  }

  createAgent(customerId: string, request: CreateAgentRequest): Observable<CustomerUserDTO> {
    return this.http.post<CustomerUserDTO>(`${environment.apiBaseUrl}/api/v1/customers/${customerId}/agents`, request);
  }

  resendInvitation(customerId: string, invitationId: string): Observable<InvitationDTO> {
    return this.http.post<InvitationDTO>(
      `${environment.apiBaseUrl}/api/v1/customers/${customerId}/users/invitations/${invitationId}/resend`,
      {},
    );
  }

  revokeInvitation(customerId: string, invitationId: string): Observable<void> {
    return this.http.delete<void>(
      `${environment.apiBaseUrl}/api/v1/customers/${customerId}/users/invitations/${invitationId}`,
    );
  }

  /** Public endpoint (no auth) — the opaque token in the path is the credential. */
  validateInvitation(token: string): Observable<PublicInvitationDTO> {
    return this.http.get<PublicInvitationDTO>(
      `${environment.apiBaseUrl}/api/v1/invitations/${encodeURIComponent(token)}`,
    );
  }
}
